// 📄 components/layout/UserMenu.tsx
// Reemplaza el botón suelto de "Logout" del navbar por un menú de perfil:
// avatar con iniciales + nombre + dropdown (perfil / configuración / salir).
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, User as UserIcon, Settings, LogOut, KeyRound } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { useAuth } from '../../context/AuthContext.tsx';
import { getDisplayName, getInitials } from '../../utils/displayName.ts';
import { listarSolicitudes } from '../../services/credencialAccesoService.ts';

export default function UserMenu() {
  const { user, profile, esClienteExterno } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // Solicitudes de credenciales por aprobar: se consultan al abrir el menú
  // (no en cada pantalla), los aprobadores además reciben un correo.
  const [porAprobar, setPorAprobar] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open || !user) return;
    let cancelado = false;
    listarSolicitudes('porAprobar')
      .then((lista) => { if (!cancelado) setPorAprobar(lista.length); })
      .catch(() => {});
    return () => { cancelado = true; };
  }, [open, user]);

  if (!user) return null;

  const displayName = getDisplayName(user, profile);
  const initials = getInitials(displayName);

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full pl-1.5 pr-3 py-1.5 transition-all"
      >
        <span className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0">
          {initials}
        </span>
        <span className="text-sm text-gray-200 max-w-[140px] truncate">{displayName}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-64 bg-gray-800 border border-white/10 rounded-xl shadow-lg overflow-hidden z-50">
          <div className="px-4 py-3">
            <p className="text-sm font-semibold text-white truncate">{displayName}</p>
            <p className="text-xs text-gray-400 truncate">{user.email}</p>
          </div>

          <div className="border-t border-white/10" />

          <button
            onClick={() => { setOpen(false); navigate('/perfil'); }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 transition-all text-left"
          >
            <UserIcon className="w-4 h-4" /> Mi perfil
          </button>

          <button
            onClick={() => { setOpen(false); navigate('/solicitudes-credenciales'); }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 transition-all text-left"
          >
            <KeyRound className="w-4 h-4" /> Solicitudes de credenciales
            {porAprobar > 0 && (
              <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300">{porAprobar}</span>
            )}
          </button>

          {profile?.role === 'admin' && !esClienteExterno && (
            <button
              onClick={() => { setOpen(false); navigate('/admin'); }}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 transition-all text-left"
            >
              <Settings className="w-4 h-4" /> Configuración
            </button>
          )}

          <div className="border-t border-white/10" />

          <button
            onClick={() => { setOpen(false); signOut(auth); }}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-all text-left"
          >
            <LogOut className="w-4 h-4" /> Cerrar sesión
          </button>
        </div>
      )}
    </div>
  );
}
